import { MakeOptional } from "@base/util/TypeUtil";
import { BeosCountryCode } from "@biz/Definitions";

export function getPlaspackCompany(): IBeosCompany {
  const company: Partial<IBeosCompany> = {
    name1: "Plaspack Netze GmbH",
    address: "Dr. Grobben-Straße 1",
    zip: "4690",
    countryCode: "A",
    city: "Schwanenstadt",
    phone: "+43 7673 2478 - 260",
    fax: "+43 7673 2478 - 266",
    email: "office@plaspack.at",
    uid: "ATU 53541207",
  };
  return company as IBeosCompany;
}

export type Gender = "M" | "F" | "D" | "C"; // Male, Female, Divers, Company

export interface IBeosUser {
  _id: string;
  id: number;
  companyId: number;
  title: string;
  firstName: string;
  lastName: string;
  function: string;
  email: string;
  phone: string;
  mobile: string;
  fax: string;
  name1: string;
  name2: string;
  name3: string;
  address: string;
  city: string;
  countryCode: BeosCountryCode;
  country: string;
  language: string;
  gender: Gender;
}

export interface IDiscount {
  group: string;
  discount: number;
  surCharge: number;
  validFrom?: Date;
  validTo?: Date;
}

export type DiscountGroups = Record<string, IDiscount>;

export enum CompanyType {
  None,
  RS_All, // alles
  SO_Other, // alles ausser A, M, C, CS, X, XS
}

export interface IBeosCompany {
  _id: number;
  uid: string;
  name1: string;
  name2: string;
  name3: string;
  address: string;
  zip: string;
  city: string;
  countryCode: BeosCountryCode;
  country: string;
  language: string;
  longitude?: number;
  latitude?: number;
  email: string;
  phone: string;
  fax: string;
  www: string;
  trader_search: boolean;
  discounts: IDiscount[];
  users: number[];
  sales?: any;
  industry1: string;
  industry2: string;
  /**
   * @deprecated
   **/
  priceListIndex: number;
  surcharge: number;
  sewInLabel: string; // label sewed in sunsail
  inflationSurchargePercent: number;
  inflationSurchargeId: string;
  termsOfPayment: TermsOfPayment;
  joiningDate: number;
}

export const termsOfPayment = {
  "1": "8 Tage 2 % Skonto     20 Tage Netto",
  "2": "8 Tage 3 % Skonto    20 Tage Netto",
  "3": "14 Tage 2 % Skonto    30 Tage Netto",
  "4": "14 Tage 3 % Skonto    30 Tage Netto",
  "8": "8 Tage 2% Skonto 30 Tage netto",
  "9": "Zahlung im Voraus 3% Skonto",
  "10": "10 Tage Netto",
  "12": "8 Tage 2 % Skonto    14 Tage Netto",
  "14": "14 Tage Netto",
  "20": "20 Tage ohne Abzug",
  "25": "Zahlung nach Erhalt",
  "22": "Dekadenweise Zahlung laut Vereinbarung",
  "28": "30 Tage 3 % Skonto    60 Tage Netto",
  "29": "Bankeinzug innerhalb 30 Tage",
  "30": "30 Tage Netto",
  "31": "7 Tage 2 % Skonto 14 Tage netto",
  "39": "Bankeinzug 42 Tage  4% Skonto",
  "40": "Bankeinzug 20 Tage netto",
  "41": "Bankeinzug 14 Tage 3% Skonto",
  "53": "Bankeinzug 30 Tage 3% Skonto",
  "55": "Bankeinzug 20 Tage 3% Skonto",
  "56": "14 Tage 2 % Skonto    20 Tage Netto",
  "63": "14 Tage 4 % Skonto    30 Tage Netto",
  "70": "Zahlung im Voraus",
  "71": "8 Tage Netto",
  "73": "Bankeinzug 20 Tag 2%",
  "75": "Fertigung erst nach Zahlungseingang",
  "78": "3% Skonto nach Erhalt der Rechnung",
  "85": "10 Tage 2 % Skonto    20 Tage Netto",
  "86": "10 Tage 2 % Skonto    30 Tage Netto",
  "87": "10 Tage 3 % Skonto    30 Tage Netto",
  "90": "90 Tage Netto"
} satisfies Record<string, string>;
export type TermsOfPayment = keyof typeof termsOfPayment;

export type PrePaymentType = "single" | "double";
export function getPrePayment(top: TermsOfPayment | undefined): PrePaymentType | undefined {
  if (top === "70" || top === "9")
    return "single";
  else if (top === "75")
    return "double"
  return undefined;
}

export function getPrePaymentPriority(top: TermsOfPayment | undefined): number {
  const sortPriority = {
    "single": 1,
    "double": 2,
  }
  const v = getPrePayment(top);
  const p = sortPriority[v as keyof typeof sortPriority];
  return p || 0;
}

export interface IBouncedMail {
  address: string;
  created_at: string;
  error: string;
}

export enum OrderState {
  Initialized = "Initialized",
  Uploaded = "Uploaded",
  Cancelled = "Cancelled",
  Ordered = "Ordered",
  Clarification = "Clarification",
  Accepted = "Accepted",
  OrderChecked = "OrderChecked",
  OrderRegistered = "OrderRegistered",
  Production = "Production",
  Shipped = "Shipped",
}



export class OrderStateHelper {
  static ge(s: OrderState, target: OrderState): boolean {
    return getSceneStateNumber(s) >= getSceneStateNumber(target);
  }
  static gt(s: OrderState, target: OrderState): boolean {
    return getSceneStateNumber(s) > getSceneStateNumber(target);
  }
}

export function isOrderedOrder(s: OrderState): boolean {
  return getSceneStateNumber(s) >= getSceneStateNumber(OrderState.Ordered);
}

export function isOrderedAndChecked(s: OrderState): boolean {
  return getSceneStateNumber(s) >= getSceneStateNumber(OrderState.OrderChecked);
}

export function getSceneStateNumber(s: OrderState): number {
  switch (s) {
    case OrderState.Initialized:
      return -2;
    case OrderState.Uploaded:
      return -1;
    case OrderState.Cancelled:
      return 0;
    case OrderState.Ordered:
      return 1;
    case OrderState.Clarification:
      return 2;
    case OrderState.Accepted:
      return 3;
    case OrderState.OrderChecked:
      return 4;
    case OrderState.OrderRegistered:
      return 5;
    case OrderState.Production:
      return 6;
    case OrderState.Shipped:
    case "Finished" as any: // old
      return 7;
  }
  return -3;
}

export function getSceneStateNameOnly(s: OrderState): string {
  switch (s) {
    case OrderState.Initialized:
      return "Initialized";
    case OrderState.Uploaded:
      return "Uploaded";
    case OrderState.Cancelled:
      return "Storniert";
    case OrderState.Ordered:
      return "Bestellt";
    case OrderState.Clarification:
      return "In Abklärung";
    case OrderState.Accepted:
      return "Entgegengenommen";
    case OrderState.OrderChecked:
      return "Geprüft";
    case OrderState.OrderRegistered:
      return "Auftrag erfasst";
    case OrderState.Production:
      return "In Produktion";
    case OrderState.Shipped:
    case "Finished" as any: // old
      return "Versendet";
  }
  return "";
}

export function getSceneStateName(s: OrderState): string {
  const number = getSceneStateNumber(s);
  const name = getSceneStateNameOnly(s);
  return `(${number}) ${name}`
}

export enum UploadType {
  Normal = "Normal",
  Order = "Order",
  Offer = "Offer",
}

export interface IHistoryOrderStateEvent {
  _id: string;
  timestamp: number;
  type: "orderState";
  sceneId: string;
  fromState: OrderState;
  toState: OrderState;
  user: string;
  info: {
    src: string;
    params?: any;
  }
}

export type HistoryEvent = IHistoryOrderStateEvent;

type ObjectIdDummy = object;

export function createOrderHistoryEvent(sceneId: string | ObjectIdDummy | undefined, fromState: OrderState, toState: OrderState, user: string, info: IHistoryOrderStateEvent["info"], options?: { _id?: string }): Omit<IHistoryOrderStateEvent, "_id"> {
  const ret: MakeOptional<IHistoryOrderStateEvent, "_id"> = {
    timestamp: Date.now(),
    type: "orderState",
    sceneId: "" + sceneId,
    fromState,
    toState,
    user,
    info
  }
  if (options?._id)
    ret._id = options?._id;
  return ret;
}

export type TrackingAppType = "app_new" | "other";

export interface ITrackingAppUsage {
  _id: string;
  startDate: number;
  endDate: number;
  user: string;
  count: number;
  appType: TrackingAppType;
  buildTime?: number;
  ip: string;
}

export function getInflationSurchargePercent(company: IBeosCompany | undefined): number {
  return company?.inflationSurchargePercent || 0;
}